import type { ColumnVisibilityState } from "@tanstack/react-table";

import type { DataTableColumn, DataTableInstance } from "./table-features";
import type { Column } from "./types";

import { getColumnId } from "./types";

/**
 * Columns the visibility panel lists: real data columns only. Action columns
 * (`__select`/`__edit`/`__delete`) carry no `meta.column` and are dropped, as
 * are filter-only columns (`hidden`), which never render by design. Sticky
 * columns stay in the list — they report `getCanHide() === false`, so the panel
 * shows them checked and disabled.
 */
export function toggleableColumns<T>(
  table: DataTableInstance<T>,
): DataTableColumn<T>[] {
  return table.getAllLeafColumns().filter((column) => {
    const meta = column.columnDef.meta?.column;
    return meta != null && !meta.hidden;
  });
}

/**
 * Shows or hides a column from the visibility panel.
 *
 * Hiding takes the column's filter and sort with it: a filter still applying
 * from a column the user can no longer see would count toward the toolbar badge
 * with nothing on screen explaining it, and an invisible sort would keep
 * ordering the rows. The trade is one-way — re-showing the column does not bring
 * the filter back, and "Show all columns" restores nothing but visibility.
 *
 * Filter-only (`hidden`) columns are untouched by this: they never reach the
 * panel, so they keep filtering without rendering, which is their whole purpose.
 */
export function setColumnVisible<T>(
  table: DataTableInstance<T>,
  column: DataTableColumn<T>,
  visible: boolean,
) {
  column.toggleVisibility(visible);
  if (visible) return;
  if (column.getFilterValue() !== undefined) column.setFilterValue(undefined);
  // State-level rather than `column.clearSorting()`: `sorting` is the slice
  // DataTable owns and persists, and this is the setter it already runs on.
  table.setSorting((prev) => prev.filter((entry) => entry.id !== column.id));
}

/**
 * Initial TanStack visibility state: filter-only columns start hidden, then a
 * persisted state (if any) is layered on top.
 *
 * Restored entries are sanitized against the current columns because
 * `getIsVisible()` reads the state map directly and ignores `enableHiding` — a
 * saved `false` for a column that has since become sticky (or was removed)
 * would otherwise hide it with no way to bring it back.
 */
export function initialColumnVisibility<T>(
  columns: Column<T>[],
  restored?: ColumnVisibilityState,
): ColumnVisibilityState {
  const visibility: ColumnVisibilityState = {};
  const hideable = new Map<string, boolean>();
  columns.forEach((col, i) => {
    const id = getColumnId(col, i);
    hideable.set(id, !col.sticky && !col.hidden);
    if (col.hidden) visibility[id] = false;
  });

  if (restored) {
    for (const [id, visible] of Object.entries(restored)) {
      if (hideable.get(id)) visibility[id] = visible;
    }
  }

  return visibility;
}
